import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtDateTime, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, Loader2 } from "lucide-react";

const actionColors = {
  session_created: "bg-accent/15 text-accent",
  session_submitted: "bg-success/15 text-success",
  email_sent: "bg-success/15 text-success",
  email_failed: "bg-destructive/15 text-destructive",
  template_created: "bg-primary/10 text-primary",
  template_edited: "bg-primary/10 text-primary",
  material_uploaded: "bg-accent/15 text-accent",
  material_replaced: "bg-warning/15 text-warning",
  material_downloaded: "bg-muted text-muted-foreground",
  user_role_changed: "bg-warning/15 text-warning",
};

export default function ActivityLog() {
  const { data = [], isLoading } = useQuery({ queryKey: ["activity"], queryFn: async () => (await api.get("/activity-log", { params: { limit: 150 } })).data });

  return (
    <div className="space-y-6" data-testid="activity-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Activity Log</h1>
        <p className="text-muted-foreground mt-1">A record of every meaningful action across the system.</p>
      </div>
      <Card className="p-2">
        {isLoading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div>
          : data.length === 0 ? <EmptyState icon={Activity} title="No activity yet" desc="Actions will appear here as your team uses the tool." />
          : (
            <div className="divide-y">
              {data.map((a) => (
                <div key={a.id} className="flex items-center gap-3 p-3" data-testid={`activity-row-${a.id}`}>
                  <Badge variant="outline" className={`${actionColors[a.action] || "bg-muted text-muted-foreground"} text-[10px] font-mono border-0`}>
                    {a.action.replace(/_/g, " ")}
                  </Badge>
                  <div className="flex-1 min-w-0 text-sm">
                    <span className="font-medium">{a.actor_name}</span>
                    <span className="text-muted-foreground"> · {a.entity_type}</span>
                    {a.metadata?.session_number && <span className="text-muted-foreground font-mono"> · {a.metadata.session_number}</span>}
                    {a.metadata?.title && <span className="text-muted-foreground"> · {a.metadata.title}</span>}
                    {a.metadata?.name && <span className="text-muted-foreground"> · {a.metadata.name}</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono whitespace-nowrap">{fmtDateTime(a.created_at)}</div>
                </div>
              ))}
            </div>
          )}
      </Card>
    </div>
  );
}
